import { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { canManage } from '../permissions';
import ClientAutocomplete from './ClientAutocomplete';
import PersonAutocomplete from './PersonAutocomplete';
import DateTimePicker from './DateTimePicker';

const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

function toInputDate(val) {
  if (!val) return '';
  return String(val).split('T')[0];
}

function toInputDateTime(val) {
  if (!val) return '';
  return String(val).replace(/:\d{2}\.\d+Z?$/, '').replace('Z', '').slice(0, 16);
}

function formatUSD(val) {
  if (!val && val !== 0) return '';
  const num = parseFloat(val);
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
}

function CurrencyInput({ name, value, onChange }) {
  const [focused, setFocused] = useState(false);
  const display = focused ? (value || '') : formatUSD(value);
  return (
    <input
      type={focused ? 'number' : 'text'}
      step="0.01"
      name={name}
      value={display}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder="$0.00"
    />
  );
}

export default function ProjectForm({ project, onSave, onCancel }) {
  const { user } = useAuth();
  // Executives and above bypass field-level "required" enforcement on every
  // column the schema permits to be NULL. ProjectName + StatusId stay required
  // for everyone (NOT NULL in precon.Projects, and ungovernable downstream
  // without them). BidNumber is auto-assigned server-side, so its visual
  // asterisk is informational only.
  const execBypass = canManage(user);
  const reqMark = execBypass ? null : <span className="required">*</span>;
  const reqAttr = !execBypass;
  const [statuses, setStatuses] = useState([]);
  const [awardError, setAwardError] = useState('');
  const [projectTypes, setProjectTypes] = useState([]);
  const [ownerTypes, setOwnerTypes] = useState([]);
  const [estimators, setEstimators] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [pes, setPEs] = useState([]);
  const [pms, setPMs] = useState([]);
  const [reviewers, setReviewers] = useState([]);

  const [form, setForm] = useState({
    BidNumber: '',
    ProjectName: '',
    ClientName: '',
    StatusId: '',
    ProjectTypeId: '',
    OwnerTypeId: '',
    ClientContactName: '',
    ClientPhone: '',
    ClientEmail: '',
    ClientAddress: '',
    ClientCity: '',
    ClientState: '',
    ClientZip: '',
    ClientWebsite: '',
    ProcoreVendorId: null,
    BidType: '',
    ContractType: '',
    Address: '',
    City: '',
    State: '',
    Zip: '',
    BuildingSqFt: '',
    SiteSqFt: '',
    WinProbability: '',
    ITBReceived: '',
    EstBidStart: '',
    EstSubmittalDate: '',
    SubDueDate: '',
    GCOwnerDeadline: '',
    BufferHours: '',
    InternalDeadline: '',
    BidWalkDate: '',
    DateBidSubmitted: '',
    DateAwarded: '',
    EstStartDate: '',
    EstCompletionDate: '',
    EstimatorId: '',
    BidCoordinatorId: '',
    ProjectExecutiveId: '',
    ProjectManagerId: '',
    FinalReviewerId: '',
    ProjectCost: '',
    AwardNumber: '',
    AwardedAmount: '',
    AwardedGC: '',
    EstActualContractValue: '',
    Distributed: false,
    Competitors: '',
    Notes: '',
    FollowUp: '',
  });

  useEffect(() => {
    api.getStatuses().then(setStatuses).catch(() => {});
    api.getProjectTypes().then(setProjectTypes).catch(() => {});
    api.getOwnerTypes().then(setOwnerTypes).catch(() => {});
    api.getEstimators().then(setEstimators).catch(() => {});
    api.getCoordinators().then(setCoordinators).catch(() => {});
    api.getPEs().then(setPEs).catch(() => {});
    api.getPMs().then(setPMs).catch(() => {});
    api.getReviewers().then(setReviewers).catch(() => {});
    if (!project) {
      fetch('/api/projects/next-bid-number', { credentials: 'same-origin' }).then(r => r.json()).then(d => setForm(f => ({...f, BidNumber: d.nextBidNumber}))).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (project) {
      setForm({
        BidNumber: project.BidNumber || '',
        ProjectName: project.ProjectName || '',
        ClientName: project.ClientName || '',
        StatusId: project.StatusId || '',
        ProjectTypeId: project.ProjectTypeId || '',
        OwnerTypeId: project.OwnerTypeId || '',
        ClientContactName: project.ClientContactName || '',
        ClientPhone: project.ClientPhone || '',
        ClientEmail: project.ClientEmail || '',
        ClientAddress: project.ClientAddress || '',
        ClientCity: project.ClientCity || '',
        ClientState: project.ClientState || '',
        ClientZip: project.ClientZip || '',
        ClientWebsite: project.ClientWebsite || '',
        ProcoreVendorId: project.ProcoreVendorId || null,
        BidType: project.BidType || '',
        ContractType: project.ContractType || '',
        Address: project.Address || '',
        City: project.City || '',
        State: project.State || '',
        Zip: project.Zip || '',
        BuildingSqFt: project.BuildingSqFt || '',
        SiteSqFt: project.SiteSqFt || '',
        WinProbability: project.WinProbability || '',
        ITBReceived: toInputDate(project.ITBReceived),
        EstBidStart: toInputDate(project.EstBidStart),
        EstSubmittalDate: toInputDate(project.EstSubmittalDate),
        SubDueDate: toInputDateTime(project.SubDueDate),
        GCOwnerDeadline: toInputDateTime(project.GCOwnerDeadline),
        BufferHours: project.BufferHours || '',
        InternalDeadline: toInputDateTime(project.InternalDeadline),
        BidWalkDate: toInputDate(project.BidWalkDate),
        DateBidSubmitted: toInputDateTime(project.DateBidSubmitted),
        DateAwarded: toInputDate(project.DateAwarded),
        EstStartDate: toInputDate(project.EstStartDate),
        EstCompletionDate: toInputDate(project.EstCompletionDate),
        EstimatorId: project.EstimatorId || '',
        BidCoordinatorId: project.BidCoordinatorId || '',
        ProjectExecutiveId: project.ProjectExecutiveId || '',
        ProjectManagerId: project.ProjectManagerId || '',
        FinalReviewerId: project.FinalReviewerId || '',
        ProjectCost: project.ProjectCost || '',
        AwardNumber: project.AwardNumber || '',
        AwardedAmount: project.AwardedAmount || '',
        AwardedGC: project.AwardedGC || '',
        EstActualContractValue: project.EstActualContractValue || '',
        Distributed: project.Distributed || false,
        IsPriority: project.IsPriority || false,
        IsArchived: project.IsArchived || false,
        Competitors: project.Competitors || '',
        Notes: project.Notes || '',
        FollowUp: project.FollowUp || '',
      });
    }
  }, [project]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  function handleSubmit(e) {
    e.preventDefault();

    // Moving a project to Awarded requires Est. Start Date, Bid Amount, and
    // Date Awarded (mirrors the server gate). Block the save with an inline
    // message listing what's missing. Applies to everyone, execs included —
    // these are award facts, not role-gated optional fields.
    const awardedStatus = statuses.find(s => s.StatusName === 'Awarded');
    if (awardedStatus && String(form.StatusId) === String(awardedStatus.StatusId)) {
      const missing = [];
      if (!form.EstStartDate) missing.push('Est. Project Start Date');
      if (!form.ProjectCost || Number(form.ProjectCost) === 0) missing.push('Bid Amount');
      if (!form.DateAwarded) missing.push('Date Awarded');
      if (missing.length) {
        setAwardError(`Cannot mark Awarded until these are filled in: ${missing.join(', ')}.`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    // Estimator + Estimating Coordinator are required for non-managers. The
    // typeahead has no native HTML5 'required', so enforce it here.
    if (!execBypass) {
      const missingTeam = [];
      if (!form.EstimatorId) missingTeam.push('Estimator');
      if (!form.BidCoordinatorId) missingTeam.push('Estimating Coordinator');
      if (missingTeam.length) {
        setAwardError(`Please assign: ${missingTeam.join(' and ')}.`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }
    setAwardError('');

    const data = { ...form };
    Object.keys(data).forEach(key => {
      if (data[key] === '') data[key] = null;
    });
    ['StatusId', 'ProjectTypeId', 'OwnerTypeId', 'EstimatorId', 'BidCoordinatorId', 'ProjectExecutiveId', 'ProjectManagerId', 'FinalReviewerId', 'BuildingSqFt', 'SiteSqFt'].forEach(k => {
      if (data[k]) data[k] = parseInt(data[k]);
    });
    ['ProjectCost', 'AwardedAmount', 'EstActualContractValue', 'BufferHours'].forEach(k => {
      if (data[k]) data[k] = parseFloat(data[k]);
    });
    onSave(data);
  }

  return (
    <form onSubmit={handleSubmit}>

      <div className="form-actions" style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--color-black)' }}>
          {project ? `Edit Project — ${project.BidNumber || ''} — ${project.ProjectName || ''}` : 'New Project'}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary">Save Project</button>
        </div>
      </div>

      {awardError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>
          {awardError}
        </div>
      )}

      {/* ─── PROJECT DETAILS ─── */}
      <div className="form-section">
        <div className="form-section-header"><div className="section-icon" style={{ background: 'var(--color-accent)' }} />Project Details</div>
        <div className="form-section-body">
          <div className="form-grid four-col">
            {/* Row 1: Identity */}
            <div className="form-group">
              <label>Bid Number <span className="required">*</span></label>
              <input name="BidNumber" value={form.BidNumber} onChange={handleChange} required />
              {!project && <div style={{ fontSize: '11px', color: 'var(--color-gray-500)', marginTop: '4px', lineHeight: 1.3 }}>Auto-assigned on save. Override to force a custom number.</div>}
            </div>
            <div className="form-group">
              <label>Project Name <span className="required">*</span></label>
              <input name="ProjectName" value={form.ProjectName} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Status <span className="required">*</span></label>
              <select name="StatusId" value={form.StatusId} onChange={handleChange} required>
                <option value="">Select status...</option>
                {statuses.map(s => <option key={s.StatusId} value={s.StatusId}>{s.StatusName}</option>)}
              </select>
            </div>
            {canManage(user) && (
              <div className="form-group">
                <label>Win Probability</label>
                <select name="WinProbability" value={form.WinProbability || ''} onChange={handleChange}>
                  <option value="">Unrated</option>
                  <option value="High">High</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Contract Type</label>
              <select name="ContractType" value={form.ContractType || ''} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="Stipulated Sum">Stipulated Sum</option>
                <option value="Cost Plus">Cost Plus</option>
                <option value="Cost Plus G-Max">Cost Plus G-Max</option>
                <option value="Design Build">Design Build</option>
                <option value="CM At Risk">CM At Risk</option>
              </select>
            </div>
            {/* Row 2: Classification + scope */}
            <div className="form-group">
              <label>Project Type {reqMark}</label>
              <select name="ProjectTypeId" value={form.ProjectTypeId} onChange={handleChange} required={reqAttr}>
                <option value="">Select type...</option>
                {projectTypes.map(t => <option key={t.ProjectTypeId} value={t.ProjectTypeId}>{t.TypeName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Bid Type {reqMark}</label>
              <select name="BidType" value={form.BidType || ''} onChange={handleChange} required={reqAttr}>
                <option value="">Select bid type...</option>
                <option value="Competitive">Competitive</option>
                <option value="Negotiated">Negotiated</option>
                <option value="Not Applicable">Not Applicable</option>
              </select>
            </div>
            <div className="form-group">
              <label>Building SqFt</label>
              <input type="number" min="0" name="BuildingSqFt" value={form.BuildingSqFt || ''} onChange={handleChange} placeholder="e.g. 12,500" />
            </div>
            <div className="form-group">
              <label>Site SqFt</label>
              <input type="number" min="0" name="SiteSqFt" value={form.SiteSqFt || ''} onChange={handleChange} placeholder="e.g. 43,560" />
            </div>
            {/* Row 3: Location */}
            <div className="form-group">
              <label>Address {reqMark}</label>
              <input name="Address" value={form.Address || ''} onChange={handleChange} required={reqAttr} placeholder="Street address" />
            </div>
            <div className="form-group">
              <label>City</label>
              <input name="City" value={form.City || ''} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>State {reqMark}</label>
              <select name="State" value={form.State || ''} onChange={handleChange} required={reqAttr}>
                <option value="">Select state...</option>
                {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Zip</label>
              <input name="Zip" value={form.Zip || ''} onChange={handleChange} maxLength={10} />
            </div>
            <div className="form-group" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', paddingTop: '22px' }}>
              <input type="checkbox" name="Distributed" checked={form.Distributed} onChange={handleChange} />
              <label style={{ margin: 0 }}>Plans Distributed</label>
            </div>
          </div>
        </div>
      </div>

      {/* ─── CLIENT / OWNER ─── */}
      <div className="form-section">
        <div className="form-section-header"><div className="section-icon" style={{ background: '#7c3aed' }} />Client / Owner</div>
        <div className="form-section-body">
          <div className="form-grid four-col">
            <div className="form-group span-two">
              <label>Client / Owner {reqMark}</label>
              <ClientAutocomplete
                value={form.ClientName}
                onChange={(val) => setForm(prev => ({ ...prev, ClientName: val }))}
                onSelect={(item) => setForm(prev => ({
                  ...prev,
                  ClientName: item.ClientName,
                  OwnerTypeId: item.OwnerTypeId || prev.OwnerTypeId,
                  ClientContactName: item.ContactName || prev.ClientContactName,
                  ClientPhone: item.Phone || prev.ClientPhone,
                  ClientEmail: item.Email || prev.ClientEmail,
                  ClientAddress: item.Address || prev.ClientAddress,
                  ClientCity: item.City || prev.ClientCity,
                  ClientState: item.State || prev.ClientState,
                  ClientZip: item.Zip || prev.ClientZip,
                  ClientWebsite: item.Website || prev.ClientWebsite,
                  ProcoreVendorId: item.ProcoreVendorId || prev.ProcoreVendorId,
                }))}
                placeholder="Search clients or type new..."
              />
            </div>
            {/* Row 1: Client (span 2) | Owner Type | (empty) */}
            <div className="form-group">
              <label>Owner Type</label>
              <select name="OwnerTypeId" value={form.OwnerTypeId || ''} onChange={handleChange}>
                <option value="">Select owner type...</option>
                {ownerTypes.map(t => <option key={t.OwnerTypeId} value={t.OwnerTypeId}>{t.TypeName}</option>)}
              </select>
            </div>
            <div className="form-group" />
            {/* Row 2: Contact Name | Phone | Email | Website */}
            <div className="form-group">
              <label>Contact Name</label>
              <input name="ClientContactName" value={form.ClientContactName || ''} onChange={handleChange} placeholder="Primary contact" />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input name="ClientPhone" value={form.ClientPhone || ''} onChange={handleChange} placeholder="(xxx) xxx-xxxx" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="ClientEmail" value={form.ClientEmail || ''} onChange={handleChange} placeholder="contact@company.com" />
            </div>
            <div className="form-group">
              <label>Website</label>
              <input name="ClientWebsite" value={form.ClientWebsite || ''} onChange={handleChange} placeholder="https://..." />
            </div>
            {/* Row 3: Address | City | State | Zip */}
            <div className="form-group">
              <label>Address</label>
              <input name="ClientAddress" value={form.ClientAddress || ''} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>City</label>
              <input name="ClientCity" value={form.ClientCity || ''} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>State</label>
              <select name="ClientState" value={form.ClientState || ''} onChange={handleChange}>
                <option value="">Select state...</option>
                {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Zip</label>
              <input name="ClientZip" value={form.ClientZip || ''} onChange={handleChange} />
            </div>
          </div>
        </div>
      </div>

      {/* Location & Scope merged into Project Details above */}

      {/* ─── TEAM ─── */}
      <div className="form-section">
        <div className="form-section-header"><div className="section-icon" style={{ background: '#0891b2' }} />Team</div>
        <div className="form-section-body">
          <div className="form-grid four-col">
            <div className="form-group">
              <label>Estimator {reqMark}</label>
              <PersonAutocomplete
                options={estimators}
                value={form.EstimatorId}
                currentLabel={project?.Estimator}
                onChange={id => setForm(f => ({ ...f, EstimatorId: id ?? null }))}
                placeholder="Search users…"
              />
            </div>
            <div className="form-group">
              <label>Estimating Coordinator {reqMark}</label>
              <PersonAutocomplete
                options={coordinators}
                value={form.BidCoordinatorId}
                currentLabel={project?.BidCoordinator}
                onChange={id => setForm(f => ({ ...f, BidCoordinatorId: id ?? null }))}
                placeholder="Search users…"
              />
            </div>
            <div className="form-group">
              <label>Project Executive</label>
              <select name="ProjectExecutiveId" value={form.ProjectExecutiveId} onChange={handleChange}>
                <option value="">Select...</option>
                {pes.map(t => <option key={t.TeamMemberId} value={t.TeamMemberId}>{t.FullName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Project Manager</label>
              <select name="ProjectManagerId" value={form.ProjectManagerId} onChange={handleChange}>
                <option value="">Select...</option>
                {pms.map(t => <option key={t.TeamMemberId} value={t.TeamMemberId}>{t.FullName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Final Reviewer</label>
              <select name="FinalReviewerId" value={form.FinalReviewerId} onChange={handleChange}>
                <option value="">Select...</option>
                {reviewers.map(t => <option key={t.TeamMemberId} value={t.TeamMemberId}>{t.FullName}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ─── SCHEDULE ─── */}
      <div className="form-section">
        <div className="form-section-header"><div className="section-icon" style={{ background: '#d97706' }} />Schedule</div>
        <div className="form-section-body">
          <div className="form-subsection-label">External Dates</div>
          <div className="form-grid four-col">
            <div className="form-group">
              <label>ITB Received {reqMark}</label>
              <DateTimePicker value={form.ITBReceived} onChange={v => setForm(f => ({ ...f, ITBReceived: v }))} required={reqAttr} />
            </div>
            <div className="form-group">
              <label>Bid Walk Date</label>
              <DateTimePicker value={form.BidWalkDate} onChange={v => setForm(f => ({ ...f, BidWalkDate: v }))} />
            </div>
            <div className="form-group">
              <label>Sub Bids Due {reqMark}</label>
              <DateTimePicker value={form.SubDueDate} onChange={v => setForm(f => ({ ...f, SubDueDate: v }))} showTime required={reqAttr} />
            </div>
            <div className="form-group">
              <label>Owner Bid Submission Deadline {reqMark}</label>
              <DateTimePicker value={form.GCOwnerDeadline} onChange={v => setForm(f => ({ ...f, GCOwnerDeadline: v }))} showTime required={reqAttr} />
            </div>
          </div>

          <div className="form-subsection-label">Internal Dates</div>
          <div className="form-grid four-col">
            <div className="form-group">
              <label>Estimator Bid Commencement Date</label>
              <DateTimePicker value={form.EstBidStart} onChange={v => setForm(f => ({ ...f, EstBidStart: v }))} />
            </div>
            <div className="form-group">
              <label>Bid Review Lead Time (hrs) {reqMark}</label>
              <select name="BufferHours" value={form.BufferHours || ''} onChange={handleChange} required={reqAttr}>
                <option value="">Select...</option>
                <option value="2">2 hours</option>
                <option value="6">6 hours</option>
                <option value="12">12 hours</option>
                <option value="24">24 hours</option>
              </select>
            </div>
            <div className="form-group">
              <label>Bid Due to Manager</label>
              <DateTimePicker value={form.InternalDeadline} onChange={v => setForm(f => ({ ...f, InternalDeadline: v }))} showTime />
            </div>
            <div className="form-group">
              <label>Date Bid Submitted to Owner</label>
              <DateTimePicker value={form.DateBidSubmitted} onChange={v => setForm(f => ({ ...f, DateBidSubmitted: v }))} showTime />
            </div>
            <div className="form-group">
              <label>Est. Project Start Date</label>
              <DateTimePicker value={form.EstStartDate} onChange={v => setForm(f => ({ ...f, EstStartDate: v }))} />
            </div>
            <div className="form-group">
              <label>Est. Project Completion Date</label>
              <DateTimePicker value={form.EstCompletionDate} onChange={v => setForm(f => ({ ...f, EstCompletionDate: v }))} />
            </div>
            <div className="form-group">
              <label>Date Awarded</label>
              <DateTimePicker value={form.DateAwarded} onChange={v => setForm(f => ({ ...f, DateAwarded: v }))} />
            </div>
          </div>
        </div>
      </div>

      {/* ─── FINANCIALS ─── */}
      <div className="form-section">
        <div className="form-section-header"><div className="section-icon" style={{ background: '#16a34a' }} />Financials</div>
        <div className="form-section-body">
          <div className="form-grid four-col">
            <div className="form-group">
              <label>Bid Amount ($)</label>
              <CurrencyInput name="ProjectCost" value={form.ProjectCost} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Projected Bid Amount ($)</label>
              <CurrencyInput name="EstActualContractValue" value={form.EstActualContractValue} onChange={handleChange} />
            </div>
          </div>
        </div>
      </div>

      {/* ─── MISCELLANEOUS ─── */}
      <div className="form-section">
        <div className="form-section-header"><div className="section-icon" style={{ background: '#6b7280' }} />Miscellaneous</div>
        <div className="form-section-body">
          <div className="form-grid four-col">
            <div className="form-group">
              <label>Awarded GC</label>
              <input name="AwardedGC" value={form.AwardedGC || ''} onChange={handleChange} />
            </div>
          </div>
          <div className="form-grid" style={{ marginTop: '12px' }}>
            <div className="form-group full-width">
              <label>Competitors</label>
              <textarea name="Competitors" value={form.Competitors || ''} onChange={handleChange} placeholder="Known competitors on this bid..." />
            </div>
            <div className="form-group full-width">
              <label>Notes</label>
              <textarea name="Notes" value={form.Notes || ''} onChange={handleChange} placeholder="General notes..." />
            </div>
            <div className="form-group full-width">
              <label>Follow Up</label>
              <textarea name="FollowUp" value={form.FollowUp || ''} onChange={handleChange} placeholder="Follow-up items..." />
            </div>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary">Save Project</button>
      </div>
    </form>
  );
}
